<script lang="ts" setup>
import { useRoute } from 'vue-router';
import { AppTypography } from '~/components/base';
import { SUPPORT_MENU, SUPPORT_SECTION_PATHS } from '~/utils/enums/navigation';

// Help Centre and About are sibling destinations sharing one chrome: the
// "Support" title, the bordered menu card, and the white content card. Each
// lives at its own route, so the menu is real links and the active cell comes
// from the current path rather than local tab state.
const { heading, flushOnMobile = false } = defineProps<{
  heading: string;
  /** Help Centre (Figma): drops the white card look on mobile so rows sit
   *  flush on the page background. */
  flushOnMobile?: boolean;
}>();

const route = useRoute();

// /contact-us is a Help Centre sub-page on its own top-level route, so
// `router-link-active` never lands on the Help Centre cell there. Match it
// explicitly so the cell stays highlighted.
function isCellActive(to: string) {
  // `route` is undefined when the shell is mounted without a router (unit
  // tests), so read the path defensively — same guard as AppTopbar.
  const path = route?.path ?? '';
  return to === '/support' && SUPPORT_SECTION_PATHS.includes(path);
}
</script>

<template>
  <div class="support-shell">
    <!-- Left column (desktop): the "Support" title sits above a bordered menu
         card. On mobile this column is hidden and the content shows directly —
         the mobile layout is designed separately in Figma. -->
    <aside class="support-shell__sidebar hidden lg:flex">
      <AppTypography type="title-5" element="h1" class="text-color-primary">
        Support
      </AppTypography>

      <nav class="support-shell__menu">
        <NuxtLink
          v-for="item in SUPPORT_MENU"
          :key="item.to"
          :to="item.to"
          :data-testid="`support-menu-${item.to.replace('/', '')}`"
          class="support-shell__cell"
          :class="{ 'support-shell__cell--active': isCellActive(item.to) }"
        >
          <Icon :name="item.icon ?? ''" size="24" class="shrink-0" />
          <AppTypography type="body" class="support-shell__cell-label">{{
            item.label
          }}</AppTypography>
        </NuxtLink>
      </nav>
    </aside>

    <div
      class="support-shell__content"
      :class="{ 'support-shell__content--flush': flushOnMobile }"
    >
      <!-- Hidden on mobile: the mobile topbar already renders the page name
           as its title, so a second heading here would duplicate it (same
           treatment as the candidates/parties pages). -->
      <AppTypography
        type="title-6"
        element="h2"
        class="support-shell__heading hidden lg:block"
      >
        {{ heading }}
      </AppTypography>

      <ul class="support-shell__list">
        <slot />
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.support-shell {
  display: flex;
  flex-direction: column;
  gap: $spacing-lg;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
    gap: $spacing-xl;
  }

  // Left column: "Support" title stacked above the bordered menu card.
  &__sidebar {
    flex-direction: column;
    gap: $spacing-xl;
    width: 384px;
    flex-shrink: 0;
  }

  &__menu {
    display: flex;
    flex-direction: column;
    padding: $spacing-lg;
    border: 1px solid $border-color;
    border-radius: $radius-lg;
    background-color: $surface-color-bg;
  }

  &__cell {
    display: flex;
    align-items: center;
    gap: $spacing-lg;
    height: 64px;
    padding: 0 $spacing-xl;
    border: none;
    border-radius: $radius-sm;
    background: transparent;
    color: $text-color-primary;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    // The menu cell IS a current-route indicator, so unlike most links in the
    // app it wants the active treatment — but branded, not the global tint.
    &.router-link-active,
    &--active {
      background-color: $brand-color;
      color: $text-color-white;
    }
  }

  &__cell-label {
    flex: 1;
    min-width: 0;
  }

  // Mobile: the rows sit in their own white card directly on the sand band,
  // padded horizontally only so each 64px row spans the card and its divider
  // runs edge to edge — the same list-card treatment used across the app.
  // Desktop turns it into the large right-hand panel beside the menu.
  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 0 $spacing-lg;
    border-radius: $radius-lg;
    background-color: $surface-color-bg;

    @media (min-width: 1024px) {
      gap: $spacing-2xl;
      padding: $spacing-5xl $spacing-2xl;
      border-radius: $radius-2xl;
      // Drop the content card down so its top edge aligns with the menu card
      // (which sits below the "Support" title: title line-height 24 + gap 24).
      margin-top: calc(24px + #{$spacing-xl});
    }

    // Help Centre (Figma): no card on mobile — rows sit flush on the page
    // background, relying on the page gutter instead of their own padding.
    // Desktop is untouched (the @media above re-applies the card there).
    &--flush {
      @media (max-width: 1023px) {
        padding: 0;
        border-radius: 0;
        background-color: transparent;
      }
    }
  }

  // Figma: text/primary is this warm near-black, not the navy-palette
  // $text-color-primary token used elsewhere in the app.
  &__heading {
    color: #201e1a;
  }

  &__list {
    display: flex;
    flex-direction: column;
  }
}
</style>
